import { fromJS, List, Map } from 'immutable';
import { TurboLiveEntity } from '../dto';
import { Delta } from '../dto/PushEnvelope';
import * as diff from 'nike-ui/src/common/push/diff';
import { keyBy } from 'lodash';
import fp from 'lodash/fp';

export function indexById(array: any[], idAttr = 'id'): Map<string, any> {
    return fromJS(keyBy(array, idAttr));
}

export interface OrderedIndex {
    id: string;
    order: number | string;
}

export const insertToOrderedIndex = (newItem: OrderedIndex) => (items: List<OrderedIndex>) => {
    if (items.find(item => item.id === newItem.id)) {
        return items;
    }
    const index = items.findIndex(
        item => item.order > newItem.order || (item.order === newItem.order && parseInt(item.id) > parseInt(newItem.id))
    );
    return items.insert(index < 0 ? items?.size : index, newItem);
};

export const removeFromOrderedIndex = (id: string) => (items: List<OrderedIndex>) => {
    const index = items.findIndex(item => id === item.id);
    if (index === -1) {
        return items;
    }
    return items.delete(index);
};

export function createOrderedIndex<T extends TurboLiveEntity>(
    entities: T[],
    attribute = 'order',
    idAttr = 'id'
): List<OrderedIndex> {
    const toIdWithOrder: (T) => OrderedIndex = e => ({ id: e[idAttr], order: e[attribute] });
    const composeOrderedList = fp.compose(
        fp.map(toIdWithOrder),
        fp.sortBy(attribute),
        fp.sortBy(e => parseInt(e.id))
    );
    return List(composeOrderedList(entities));
}

export const orderedIndexToIdsList = (orderedIndex: List<OrderedIndex>) => orderedIndex?.map(({ id }) => id);

export const updateIndexedById = (delta: Delta, indexFieldName: string, label?: string) => turboLiveDb => {
    label = label || indexFieldName;
    if (!turboLiveDb) {
        console.error(`parent db does not exist for ${label}`, { delta });
        return turboLiveDb;
    }
    return turboLiveDb.update(indexFieldName, entities => {
        if (!entities) {
            console.warn(`${label} does not exist in db, cannot handle UPDATE push event`, {
                delta,
                turboLiveDb
            });
            return entities;
        }
        return entities.update(delta.id, existing => {
            if (!existing) {
                console.warn(`${label} does not contain id: ${delta.id}, cannot handle UPDATE push event`, {
                    delta,
                    turboLiveDb
                });
                return existing;
            }
            return diff.applyDelta(delta.diff, existing);
        });
    });
};

export const concatWithoutDuplicates = (left: List<string>, right: List<string>): List<string> =>
    left && right ? left.concat(right.filter(r => left.indexOf(r) < 0)) : right || left || List();

export const mergeOrderedIndex = (left: List<OrderedIndex>, right: List<OrderedIndex>) => {
    return mergeWithoutDuplicates(left, right)
        .sortBy(orderedIndex => orderedIndex.order)
        .toList();
};

function mergeWithoutDuplicates(left: List<OrderedIndex>, right: List<OrderedIndex>) {
    return left.concat(right.filterNot(r => left.some(l => l.id === r.id))).toList();
}

export function indexGroupedAndOrdered(items: TurboLiveEntity[], groupByAttr: string, orderAttr?: string) {
    const composeGroupedAndOrdered = fp.compose(
        fp.mapValues((items: TurboLiveEntity[]) => createOrderedIndex(items, orderAttr || 'order')),
        fp.groupBy(groupByAttr)
    );

    return fromJS(composeGroupedAndOrdered(items));
}
